import { FC } from 'react';

import { SearchIcon } from '@heroicons/react/outline';
import {
  DateField,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';

interface SearchFormProps {
  fullCode: string;
  onFullCodeChange: (v: string) => void;
  phoneNumber: string;
  onPhoneNumberChange: (v: string) => void;
  ordererName: string;
  onOrdererNameChange: (v: string) => void;
  ordererPhone: string;
  onOrdererPhoneChange: (v: string) => void;
  redeemStatus: GiftCouponRedeemStatus[];
  onRedeemStatusChange: (v: GiftCouponRedeemStatus[]) => void;
  shareStatus: string;
  onShareStatusChange: (v: string) => void;
  dateField: DateField;
  onDateFieldChange: (v: DateField) => void;
  dateFrom: string;
  onDateFromChange: (v: string) => void;
  dateTo: string;
  onDateToChange: (v: string) => void;
  onSearch: () => void;
  onReset: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

export const SearchForm: FC<SearchFormProps> = ({
  fullCode,
  onFullCodeChange,
  phoneNumber,
  onPhoneNumberChange,
  ordererName,
  onOrdererNameChange,
  ordererPhone,
  onOrdererPhoneChange,
  redeemStatus,
  onRedeemStatusChange,
  shareStatus,
  onShareStatusChange,
  dateField,
  onDateFieldChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  onSearch,
  onReset,
  onKeyDown,
}) => {
  return (
    <div className="mx-4 rounded-lg bg-white p-4 shadow">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">쿠폰번호</label>
          <input
            type="text"
            value={fullCode}
            onChange={(e) => onFullCodeChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="쿠폰번호 입력"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            수신자 전화번호
          </label>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => onPhoneNumberChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="010-1234-5678"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">주문자명</label>
          <input
            type="text"
            value={ordererName}
            onChange={(e) => onOrdererNameChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="홍길동"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            주문자 전화번호
          </label>
          <input
            type="tel"
            value={ordererPhone}
            onChange={(e) => onOrdererPhoneChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="010-1234-5678"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">교환 상태</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {[
              { value: GiftCouponRedeemStatus.Available, label: '교환가능' },
              { value: GiftCouponRedeemStatus.Requested, label: '신청됨' },
              { value: GiftCouponRedeemStatus.ReselectRequired, label: '재선택 안내' },
              { value: GiftCouponRedeemStatus.Delivered, label: '발송완료' },
              { value: GiftCouponRedeemStatus.Rejected, label: '발송불가' },
              { value: GiftCouponRedeemStatus.Expired, label: '만료' },
              { value: GiftCouponRedeemStatus.Refunded, label: '환불' },
              { value: GiftCouponRedeemStatus.Canceled, label: '취소' },
              { value: GiftCouponRedeemStatus.Disposed, label: '폐기' },
            ].map((item) => (
              <label
                key={item.value}
                className={`inline-flex cursor-pointer items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
                  redeemStatus.includes(item.value)
                    ? 'border-indigo-400 bg-indigo-50 text-indigo-700'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={redeemStatus.includes(item.value)}
                  onChange={() => {
                    if (redeemStatus.includes(item.value)) {
                      onRedeemStatusChange(redeemStatus.filter((s) => s !== item.value));
                    } else {
                      onRedeemStatusChange([...redeemStatus, item.value]);
                    }
                  }}
                  className="hidden"
                />
                {item.label}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">공유 상태</label>
          <select
            value={shareStatus}
            onChange={(e) => onShareStatusChange(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">전체</option>
            <option value={GiftCouponShareStatus.NotShared}>미전달</option>
            <option value={GiftCouponShareStatus.Shared}>공유됨</option>
            <option value={GiftCouponShareStatus.Viewed}>조회됨</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">날짜 기준</label>
          <select
            value={dateField}
            onChange={(e) => onDateFieldChange(e.target.value as DateField)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="requestedAt">교환 신청일</option>
            <option value="deliveredAt">발송 완료일</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">시작일</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">종료일</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onDateToChange(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div>
          {/* 라벨 높이만큼 여백을 둬 검색/초기화 버튼을 종료일 등 입력칸과 같은 줄에 맞춤 */}
          <label
            aria-hidden="true"
            className="block select-none text-sm font-medium text-transparent"
          >
            &nbsp;
          </label>
          <div className="mt-1 flex items-center gap-2">
            <button
              onClick={onSearch}
              className="flex items-center gap-2 rounded-md bg-indigo-600 px-6 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <SearchIcon className="h-4 w-4" />
              검색
            </button>
            <button
              onClick={onReset}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              초기화
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
